import { filterClasses } from "@swivl-mono/helpers";
import { type ProfileFormData, type ProfileFormField, type StructuredActionTaken } from "@swivl-mono/types";
import { useState } from "react";

import type { ChatMessage } from "../../Types/BotChat.Types";

interface ProfileFormProps {
  data: ProfileFormData;
  message: ChatMessage;
  /** Called when the user submits the form. Wired through useChat → sendStructuredAction. */
  onAction?: (action: StructuredActionTaken) => void;
  /** Read-only mode for the agent timeline. */
  readOnly?: boolean;
}

const DEFAULT_TITLE = "Personal Information";
const DEFAULT_SUBMIT_LABEL = "Submit";

/**
 * `profile-form` structured-message renderer.
 *
 * Matches Figma `siWi27jxevcvcbFeLOqwDi` nodes 108:12936 (input) and
 * 108:13142 (submitted). Card with white background, grey-200 border,
 * 8px corner radius. Input mode shows editable fields with gradient-bg
 * text inputs (bottom-of-stack white, top of grey-50) and a primary
 * Submit button. Submitted mode shows a colored header bar and a
 * read-only summary of what the user entered.
 *
 * On submit, fires `onAction({ type: "submitted", target: <values JSON> })`.
 * The server-side handler captures the form values and feeds them into
 * `capture_lead` per the `intent` field.
 *
 * See docs/snowy/plans/2026-05-09-run-84-eval-triage-plan.md Cluster 4.1.
 */
export function ProfileForm({ data, message, onAction, readOnly = false }: ProfileFormProps): React.ReactElement {
  const action = message.actionTaken as StructuredActionTaken | undefined;
  const alreadySubmitted = action?.type === "submitted";
  const submittedValues = parseSubmittedValues(action);

  const fields = data.fields.length > 0 ? data.fields : DEFAULT_FIELDS;
  const isSubmittedMode = data.mode === "submitted" || alreadySubmitted;
  const valuesForReadOnly = isSubmittedMode ? (submittedValues ?? data.values ?? {}) : {};

  if (isSubmittedMode) {
    return <SubmittedCard title={data.title ?? DEFAULT_TITLE} fields={fields} values={valuesForReadOnly} />;
  }

  return <InputCard data={data} fields={fields} readOnly={readOnly} onAction={onAction} />;
}

interface InputCardProps {
  data: ProfileFormData;
  fields: ProfileFormField[];
  readOnly: boolean;
  onAction?: (action: StructuredActionTaken) => void;
}

function InputCard({ data, fields, readOnly, onAction }: InputCardProps): React.ReactElement {
  const [values, setValues] = useState<Record<string, string>>({});

  const handleChange = (id: string, value: string): void => {
    setValues((prev) => ({ ...prev, [id]: value }));
  };

  const requiredFields = fields.filter((f) => f.required !== false);
  const allRequiredFilled = requiredFields.every((f) => (values[f.id] ?? "").trim().length > 0);

  const handleSubmit = (event?: React.FormEvent): void => {
    event?.preventDefault();
    if (readOnly || !allRequiredFilled) return;
    // Per StructuredActionTaken contract: snapshot carries the submitted
    // values; target is omitted on `submitted` actions.
    onAction?.({
      type: "submitted",
      takenAt: new Date().toISOString(),
      snapshot: { intent: data.intent, values }
    });
  };

  return (
    // A real <form> so Enter submits from any field (keyboard-only flow —
    // Chuck, 2026-08-17); the button is type="submit" for the same reason.
    // Cmd/Ctrl+Enter also submits — a modified Enter is NOT a native submit,
    // and it's the muscle-memory combo from multiline boxes (Chuck).
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          handleSubmit();
        }
      }}
      className="flex flex-col gap-4 rounded-lg border border-grey-200 bg-white p-3"
    >
      {fields.map((field) => (
        <FieldRow key={field.id} field={field} value={values[field.id] ?? ""} onChange={handleChange} disabled={readOnly} />
      ))}
      <button
        type="submit"
        disabled={readOnly || !allRequiredFilled}
        className={filterClasses(
          "flex h-10 w-full items-center justify-center rounded bg-primary-500 px-3 text-base font-semibold text-white transition-opacity",
          readOnly || !allRequiredFilled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-primary-600"
        )}
      >
        {data.submitLabel ?? DEFAULT_SUBMIT_LABEL}
      </button>
    </form>
  );
}

interface FieldRowProps {
  field: ProfileFormField;
  value: string;
  onChange: (id: string, value: string) => void;
  disabled: boolean;
}

function FieldRow({ field, value, onChange, disabled }: FieldRowProps): React.ReactElement {
  const inputType = field.inputType ?? defaultInputType(field.id);
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={`profile-${field.id}`} className="text-sm font-semibold text-grey-500">
        {field.label}
      </label>
      <input
        id={`profile-${field.id}`}
        type={inputType}
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
        // 16px on mobile stops iOS Safari zooming on focus; 14px on desktop.
        className="h-[34px] w-full rounded border border-grey-300 bg-linear-to-b from-grey-50 via-white via-22% to-white px-3 py-2 text-base text-grey-900 placeholder:text-grey-500 focus:border-secondary focus:ring-2 focus:ring-secondary/30 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
      />
    </div>
  );
}

interface SubmittedCardProps {
  title: string;
  fields: ProfileFormField[];
  values: Partial<Record<string, string>>;
}

function SubmittedCard({ title, fields, values }: SubmittedCardProps): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="bg-primary-500 px-3 py-3">
        <p className="font-poppins text-base font-semibold text-white">{title}</p>
      </div>
      <div className="flex flex-col gap-4 px-3 pt-3 pb-3">
        {fields.map((field) => {
          const v = values[field.id]?.trim();
          if (!v && field.required === false) return null; // hide empty optionals on the submitted card
          return (
            <div key={field.id} className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-grey-500">{field.label}</p>
              <p className="text-sm text-grey-900">{v || <span className="text-grey-400">—</span>}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function parseSubmittedValues(action: StructuredActionTaken | undefined): Partial<Record<string, string>> | undefined {
  if (!action || action.type !== "submitted") return undefined;
  // Primary: the submit handler stores values on the snapshot (target is
  // deliberately omitted on `submitted` actions) — the parser used to read
  // ONLY target, so the submitted card always rendered dashes (Chuck,
  // 2026-08-17).
  const snapshot = action.snapshot as { values?: unknown } | undefined;
  if (snapshot && typeof snapshot === "object" && snapshot.values && typeof snapshot.values === "object") {
    return snapshot.values as Partial<Record<string, string>>;
  }
  // Legacy rows: values JSON-encoded into target.
  if (typeof action.target !== "string") return undefined;
  try {
    const parsed = JSON.parse(action.target);
    if (parsed && typeof parsed === "object" && parsed.values && typeof parsed.values === "object") {
      return parsed.values as Partial<Record<string, string>>;
    }
  } catch {
    // Malformed payload — treat as no values rather than throwing.
  }
  return undefined;
}

function defaultInputType(id: ProfileFormField["id"]): "text" | "email" | "tel" | "date" {
  switch (id) {
    case "email":
      return "email";
    case "phone":
      return "tel";
    case "moveInDate":
      return "date";
    default:
      return "text";
  }
}

const DEFAULT_FIELDS: ProfileFormField[] = [
  { id: "name", label: "Name", placeholder: "Your Name", required: true },
  { id: "email", label: "Email", placeholder: "name@email.com", required: true },
  { id: "phone", label: "Phone (Optional)", placeholder: "415-555-0000", required: false }
];
